import { useState, useEffect, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

/** An unmatched register row (GET /v1/payments/reconciliation/unmatched). */
interface Unmatched {
  transactionId: string;
  utrNumber: string | null;
  bankReferenceNo: string | null;
  beneficiaryName: string | null;
  systemPaymentAmount: number;
}

/** What the bank statement says of the payment, as entered for a manual match. */
interface Statement {
  bankStatementRef: string;
  bankStatementDate: string;
  bankCreditAmount: string;
}

const EMPTY: Statement = { bankStatementRef: '', bankStatementDate: '', bankCreditAmount: '' };

/**
 * L15.07 — Reconciliation: a payment is matched to the line of the bank
 * statement that shows it. The statement's reference, date and amount are
 * entered, so the match records what the bank says and any variance is
 * measured against the payment.
 */
export function ReconciliationPage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [items, setItems] = useState<Unmatched[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matching, setMatching] = useState<string | null>(null);
  const [statement, setStatement] = useState<Statement>(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiRequest<Unmatched[]>('/v1/payments/reconciliation/unmatched', { token })
      .then(setItems)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const ready = statement.bankStatementRef.trim() && statement.bankStatementDate && statement.bankCreditAmount.trim();

  const handleMatch = async (transactionId: string) => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/v1/payments/reconciliation/match`, {
        token, method: 'POST',
        body: {
          transactionId, matchMethodCode: 'MANUAL',
          bankStatementRef: statement.bankStatementRef.trim(),
          bankStatementDate: statement.bankStatementDate,
          bankCreditAmount: Number(statement.bankCreditAmount),
        },
      });
      setItems(prev => prev.filter(i => i.transactionId !== transactionId));
      setMatching(null);
      setStatement(EMPTY);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const set = (k: keyof Statement) => (v: string) => setStatement((s) => ({ ...s, [k]: v }));

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('finance.recon.title')}</h1>
      {error && <p style={{ color: 'red' }} role="alert">{error}</p>}
      {loading && <p>{t('finance.recon.loading')}</p>}
      {!loading && items.length === 0 && <p>{t('finance.recon.empty')}</p>}
      {items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-muted)', borderBottom: '1px solid var(--color-border-default)' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.recon.colUtr')}</th>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.recon.colVendor')}</th>
              <th style={{ padding: 8, textAlign: 'right' }}>{t('finance.recon.colAmount')}</th>
              <th style={{ padding: 8 }}>{t('finance.recon.colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.transactionId} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{row.utrNumber ?? row.bankReferenceNo ?? '\u2014'}</td>
                <td style={{ padding: 8 }}>{row.beneficiaryName ?? '\u2014'}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{money(row.systemPaymentAmount)}</td>
                <td style={{ padding: 8 }}>
                  {matching === row.transactionId ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input aria-label="statement-ref" placeholder={t('finance.recon.statementRef')} value={statement.bankStatementRef} onChange={(e) => set('bankStatementRef')(e.target.value)} />
                      <DateInput aria-label="statement-date" type="date" value={statement.bankStatementDate} onValueChange={set('bankStatementDate')} />
                      <input aria-label="statement-amount" type="number" step="0.01" placeholder={t('finance.recon.statementAmount')} value={statement.bankCreditAmount} onChange={(e) => set('bankCreditAmount')(e.target.value)} />
                      <button type="button" disabled={busy || !ready} onClick={() => void handleMatch(row.transactionId)}>{t('finance.recon.match')}</button>
                      <button type="button" onClick={() => { setMatching(null); setStatement(EMPTY); }}>{t('finance.recon.cancel')}</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => { setMatching(row.transactionId); setStatement(EMPTY); }}>{t('finance.recon.match')}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
